import { ButtonHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
  loadingText?: string;
  as?: "button" | "a";
  href?: string;
}

export default function Button({ 
  children, 
  variant = "primary", 
  size = "md",
  loading = false,
  loadingText,
  as = "button",
  href,
  className = "",
  disabled,
  ...props 
}: ButtonProps) {
  const isDisabled = disabled || loading;

  const baseClasses = "inline-flex items-center justify-center font-semibold no-underline transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#ffdd00] focus-visible:outline-offset-2";
  
  const variantClasses = {
    primary: "rounded-lg bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900",
    secondary: "rounded-lg border border-slate-300 bg-white text-slate-950 shadow-sm hover:bg-slate-50 active:bg-slate-100",
    ghost: "rounded-lg text-slate-700 hover:bg-slate-100 active:bg-slate-200",
    danger: "rounded-lg bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800",
  };

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm min-h-[44px]",
    md: "px-4 py-2.5 text-sm min-h-[44px]",
  };

  const disabledClasses = isDisabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "";

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`.trim();

  const content = loading ? (loadingText || children) : children;

  if (as === "a" && href) {
    return (
      <a href={href} className={combinedClasses} aria-disabled={isDisabled || undefined}>
        {content}
      </a>
    );
  }

  return (
    <button 
      className={combinedClasses} 
      disabled={isDisabled} 
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </button>
  );
}
